import styled, { css } from 'styled-components';

import type { HeaderThemeKeys } from '@/theme/types/components';

import { ReactComponent as Accessibility } from '@/assets/icons/accessibility-icon.svg';
import { ReactComponent as Account } from '@/assets/icons/account-icon.svg';
import { ReactComponent as Cart } from '@/assets/icons/cart-icon.svg';
import { ReactComponent as Heart } from '@/assets/icons/heart-stroke-nav.svg';
import { ReactComponent as Search } from '@/assets/icons/search-icon.svg';

export const NavicationMenu = styled.ul<{ $isAside?: boolean }>`
  display: flex;
  align-items: center;
  justify-self: end;
  list-style: none;
  li {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  path {
    -webkit-transition: 0.2s;
    transition: 0.2s;
  }

  ${({
    theme: {
      breakpoints,
      components: { navigationMenu },
    },
  }) =>
    css`
      gap: ${navigationMenu.gap};

      @media (max-width: ${breakpoints.md}) {
        gap: 1rem;
        grid-row: 1;
        grid-column: 3;

        .navigationmenu-search {
          display: none;
        }
      }

      @media (max-width: 375px) {
        gap: 0.5rem;
      }
    `}

  svg {
    cursor: pointer;
    transition: color 0.2s;
    color: ${({ $isAside, theme: { colors } }) => $isAside && colors.white};
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

export const HeartOutline = styled(Heart)<{
  $headerBackground: HeaderThemeKeys;
  $isHeightLight?: boolean;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 24px;
  height: 24px;
  position: relative;
  bottom: -2px;
  ${({
    $headerBackground,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) => css`
    &:hover {
      path {
        fill: ${headerTheme[$headerBackground].default.icon.color};
      }
    }
  `}
  ${({
    $headerBackground,
    $isHeightLight,
    $isShrunk,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) => css`
    ${$isShrunk
      ? css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
            fill: transparent;
          }
        `
      : css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
            fill: transparent;
          }
        `}
    ${$isHeightLight &&
    css`
      path {
        fill: ${headerTheme[$headerBackground].default.icon.color};
      }
    `}
  `}
`;
export const AccessibilityIcon = styled(Accessibility)<{
  $headerBackground: HeaderThemeKeys;
}>`
  width: 24px;
  height: 24px;
  vertical-align: top;
  ${({
    $headerBackground,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
    theme,
  }) => css`
    path:first-child {
      fill: ${headerTheme[$headerBackground].accessibilityIcon.path1.color};
    }
    path:last-child {
      fill: ${headerTheme[$headerBackground].accessibilityIcon.path2.color};
    }
    &:hover {
      path:first-child {
        fill: ${headerTheme[$headerBackground].accessibilityIcon.path1.color};
      }
    }
    @media (max-width: ${theme.breakpoints.md}) {
      display: none;
    }
  `}
`;
export const SearchIcon = styled(Search)<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 24px;
  height: 24px;
  ${({
    $headerBackground,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) => css`
    &:hover {
      path {
        stroke: ${headerTheme[$headerBackground].default.icon.color};
      }
    }
  `}
  ${({
    $headerBackground,
    $isShrunk,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) =>
    $isShrunk
      ? css`
          path {
            fill: ${headerTheme[$headerBackground].default.icon.color};
          }
        `
      : css`
          path {
            fill: ${headerTheme[$headerBackground].default.icon.color};
          }
        `}
`;
export const AccountIcon = styled(Account)<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 24px;
  height: 24px;
  transition: fill 0.2s, stroke 0.2s;
  ${({
    $headerBackground,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) => css`
    &:hover {
      path {
        fill: ${headerTheme[$headerBackground].default.icon.color};
      }
    }
  `}
  ${({
    $headerBackground,
    $isShrunk,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) =>
    $isShrunk
      ? css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
            fill: transparent;
          }
        `
      : css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
            fill: transparent;
          }
        `}
`;
export const CartIcon = styled(Cart)<{
  $headerBackground: HeaderThemeKeys;
  $isBagEmpty: boolean;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 24px;
  height: 24px;
  position: relative;
  bottom: 1px;
  ${({
    $headerBackground,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) => css`
    &:hover {
      path {
        fill: ${headerTheme[$headerBackground].default.icon.color};
      }
      path:nth-child(2) {
        fill: transparent;
      }
    }
  `}
  ${({
    $headerBackground,
    $isShrunk,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) =>
    $isShrunk
      ? css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
            fill: transparent;
          }
        `
      : css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
            fill: transparent;
          }
        `}
`;
export const Link = styled.a`
  text-decoration: none;
`;
